import {
    cssVar,
    HStack,
    NavBottomDrawer,
    NavItem,
    NavItems,
    VStack,
    Box,
    Text,
    Nav,
} from '@cad-arc';
import { NavLink as NavLinkBase } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useEnabledFeatures } from '../common/hooks/enabled-features/use-enabled-features';
import { useIsDexEnabled } from '../data-exchange/use-dex-access-guard';
import { FormattedDurationSince } from '../common/components/formatted-duration-since';
import { useUnitStatusColors } from '../configurable-colors-sounds/unit-status-colors';
import { useCurrentUser } from '../current-user/current-user';
import { useDrawerControls, useDrawerRender } from '../drawers/components/drawer-context-provider';
import {
    ConnectionStatusIndicator,
    connectionStatusStyles,
} from '../layout/components/connection-status-indicator';
import { HamburgerMenu } from '../layout/components/hamburger-menu';
import {
    EVENT_SEARCH_WINDOW_PATH,
    FR_EVENTS_LIST_PATH,
    MAP_WINDOW_PATH,
} from '../router/constants';
import { useUnitAssignment } from '../unit-assignment/unit-assignment-context';
import { MessagingNavItem } from './components/messaging-nav-item';
import { DexNavItem } from './components/dex-nav-item';
import { UnitLocationNavItem } from './components/unit-location-nav-item';
import { useConnectionStatus } from '../connection-status/use-connection-status';
import { UnitsListNavItem } from './components/units-list-nav-item';
import { ConnectionStatusEnumType } from '@mark43/cad-api';
import { useApplicationType } from '../common/hooks/use-application-type';
import { electronBridge } from '../electron-bridge/electron-bridge';
import { UnitEmergencyButton } from '../unit-emergency/unit-emergency-button';
import { FrNavItemLink } from './components/fr-nav-item-link';
import { globalAttributes } from '../common/global-attributes';
import { useGetDisplayedShortcutKeyByCommand } from './components/use-shortcut-key-by-command';
import { EventDetailsNavItem } from './components/event-details-nav-item';
import { useNavDrawers } from './components/use-nav-drawer';
import { useExportNotifications } from '../exports/use-exports-notifications';
import { useCallback } from 'react';
import { FrExportsNotificationsCounter } from './components/fr-exports-notification-counter';
import { Tooltip } from '~/common/components/tooltip';
import { useNonExpiredExports } from '~/exports/use-non-expired-exports';
import { DataPrivacy } from '../common/components/datadog-privacy';

function NavConnectionStatus() {
    const connectionStatus = useConnectionStatus();
    let connectionStatusToUse: ConnectionStatusEnumType = 'ONLINE';
    for (const connectionStatusValue of Object.values(connectionStatus).map(
        (status) => status
    ) as ConnectionStatusEnumType[]) {
        if (connectionStatusValue === 'OFFLINE') {
            connectionStatusToUse = 'OFFLINE';
            break;
        }
        if (connectionStatusValue === 'DEGRADED') {
            connectionStatusToUse = 'DEGRADED';
        }
    }
    return (
        <Box
            userSelect="none"
            mx="2"
            justifyContent="center"
            alignItems="center"
            display="flex"
            mb="1"
            borderRadius="md"
            px={1}
            backgroundColor="positive.accent"
            style={{
                backgroundColor: cssVar(connectionStatusStyles[connectionStatusToUse].bg as any),
            }}
            data-testid="nav-connection-status"
        >
            <HStack style={{ gap: 0, overflow: 'hidden' }}>
                <ConnectionStatusIndicator>
                    <Tooltip overlay={connectionStatusToUse}>
                        <Text
                            fontWeight="semibold"
                            fontSize="xs"
                            style={{
                                color: cssVar(
                                    connectionStatusStyles[connectionStatusToUse].fg as any
                                ),
                                marginLeft: '4px',
                                flexGrow: 1,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            <DataPrivacy privacy="unmask">{connectionStatusToUse}</DataPrivacy>
                        </Text>
                    </Tooltip>
                </ConnectionStatusIndicator>
            </HStack>
        </Box>
    );
}

function NavUser() {
    const currentUser = useCurrentUser();
    const userInitials = `${currentUser.firstName[0].toUpperCase()}${currentUser.lastName[0].toUpperCase()}`;
    const callSign = useUnitAssignment()?.callSign;
    return (
        <Box
            w="100%"
            justifyContent="center"
            alignItems="center"
            mb="1"
            display="flex"
            userSelect="none"
        >
            <Box
                cursor="pointer"
                w="60px"
                h="60px"
                _hover={{
                    backgroundColor: 'var(--arc-colors-interactive-hover)',
                }}
                borderRadius="md"
                display="flex"
                justifyContent="center"
                alignItems="center"
            >
                <Box
                    w="42px"
                    h="42px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="24px"
                    backgroundColor="brand.default"
                >
                    <Text
                        fontWeight="semibold"
                        fontSize="calc(2rem / 2.5)"
                        lineHeight="2rem"
                        style={{ color: cssVar('arc.colors.brand.content') }}
                    >
                        <DataPrivacy privacy={callSign ? 'unmask' : 'mask'}>{callSign ?? userInitials}</DataPrivacy>
                    </Text>
                </Box>
            </Box>
        </Box>
    );
}

const NavUnitStatus = observer(function NavUnitStatus() {
    const unitAssignment = useUnitAssignment();
    const unitStateData = unitAssignment?.unitStateData;
    const drawerControls = useDrawerControls();
    const drawerRender = useDrawerRender();
    const unitStatusColor = useUnitStatusColors(unitStateData?.id, unitStateData?.unitStateGroup);
    if (!unitAssignment || !unitStateData) return <></>;
    return (
        <Box
            userSelect="none"
            w="100%"
            justifyContent="center"
            alignItems="center"
            mb="1"
            display="flex"
            mt="1"
            onClick={() => {
                if (drawerRender?.drawer.type === 'fr-unit-status') {
                    drawerControls.close();
                } else {
                    drawerControls.open({ type: 'fr-unit-status', props: {} });
                }
            }}
            data-testid="nav-unit-status"
        >
            <VStack
                h="60px"
                w="60px"
                borderRadius="md"
                bgColor={unitStateData ? unitStatusColor.fill : 'surface.canvas'}
                padding="1"
                cursor="pointer"
                alignItems="center"
                justifyContent="center"
            >
                <Text
                    data-testid="unit-status"
                    fontSize="2xl"
                    fontWeight="semibold"
                    lineHeight={1}
                    style={{ color: unitStatusColor.text }}
                >
                    <DataPrivacy privacy="unmask">{unitStateData?.abbreviation ?? 'N/A'}</DataPrivacy>
                </Text>
                <div data-testid="unit-status-time">
                    <DataPrivacy privacy="unmask">
                        {unitAssignment?.unitStatusUpdatedDateUtc ? (
                            <FormattedDurationSince
                                date={
                                    new Date(
                                        unitAssignment?.unitStatusUpdatedDateUtc ??
                                            new Date().toUTCString()
                                    )
                                }
                                fontSize="md"
                                lineHeight={1}
                                style={{ color: unitStatusColor.text }}
                            />
                        ) : (
                            <Text fontSize="md" lineHeight={1} style={{ color: unitStatusColor.text }}>
                                n/a
                            </Text>
                        )}
                    </DataPrivacy>
                </div>
            </VStack>
        </Box>
    );
});

export function NavLink(props: React.ComponentProps<typeof NavLinkBase>) {
    return <NavLinkBase {...props} style={{ textDecoration: 'none', color: 'unset' }} />;
}

export function FrNavigation() {
    const { isElectron } = useApplicationType();
    const enabledFeatures = useEnabledFeatures();
    const unitAssignment = useUnitAssignment();
    const unitId = unitAssignment?.unitId;
    const { currentExports } = useNonExpiredExports();
    const { toggleDrawer } = useNavDrawers('export');
    const { events, state } = useExportNotifications(currentExports);
    const isPolice =
        unitAssignment?.unitAgencyTypeGlobalAttrId === globalAttributes.agencyTypeGlobal.POLICE;
    const { isDexEnabled } = useIsDexEnabled();
    const isDexVisible = isDexEnabled && isPolice;

    const onExportItemClick = useCallback(() => {
        toggleDrawer();
        // clean the notification about the new export items (the counter)
        events.onPopoverClose();
    }, [events, toggleDrawer]);

    const getDisplayedShortcutKeyByCommand = useGetDisplayedShortcutKeyByCommand();
    return (
        <Nav isAlwaysCollapsed data-testid="fr-nav-component">
            <NavConnectionStatus />
            <NavItems>
                <EventDetailsNavItem />
                <FrNavItemLink
                    to={FR_EVENTS_LIST_PATH}
                    icon="Queues"
                    label={getDisplayedShortcutKeyByCommand('UNASSIGNED_EVENTS')}
                    data-testid="events-list-nav-link-nav"
                />
                <FrNavItemLink
                    to={MAP_WINDOW_PATH}
                    icon="Map"
                    label={getDisplayedShortcutKeyByCommand('FIRST_RESPONDER_MAP')}
                    data-testid="map-nav-link-nav"
                />
                <FrNavItemLink
                    to={EVENT_SEARCH_WINDOW_PATH}
                    icon="SearchEvents"
                    label={getDisplayedShortcutKeyByCommand('CAD_SEARCH')}
                    data-testid="event-search-link-nav"
                />
                <UnitsListNavItem label={getDisplayedShortcutKeyByCommand('UNITS')} />
                {unitId && enabledFeatures.firstResponderCanEditUnitLocations && (
                    <UnitLocationNavItem
                        label={getDisplayedShortcutKeyByCommand('UPDATE_UNIT_LOCATION')}
                    />
                )}
                {isDexVisible && (
                    <DexNavItem
                        label={getDisplayedShortcutKeyByCommand(
                            'DATA_EXCHANGE_OPEN_FORM_VEHICLE_BY_PLATE'
                        )}
                    />
                )}
                {enabledFeatures.messaging && <MessagingNavItem />}
            </NavItems>
            <NavBottomDrawer
                style={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
                data-testid="nav-bottom-drawer"
            >
                {process.env.NODE_ENV === 'development' && isElectron ? (
                    <NavItems>
                        <NavItem
                            icon="CollapseWindow"
                            label="Fix Size"
                            onClick={() => {
                                if (electronBridge && 'isElectronRewrite' in electronBridge) {
                                    electronBridge.setFrWindowSize();
                                }
                            }}
                        />
                    </NavItems>
                ) : undefined}

                <NavUnitStatus />
                <HamburgerMenu
                    side="right"
                    trigger={
                        <HStack
                            data-testid="hamburger-menu"
                            margin={0}
                            padding={0}
                            position="relative"
                        >
                            <NavUser />
                            <FrExportsNotificationsCounter
                                notificationCount={state.notificationCount}
                                style={{
                                    right: '10%',
                                }}
                            />
                        </HStack>
                    }
                    frExportsMenuItemProps={{
                        notificationCount: state.notificationCount,
                        onExportItemClick,
                    }}
                />
                {enabledFeatures.unitEmergencyEnabled && isPolice && <UnitEmergencyButton />}
            </NavBottomDrawer>
        </Nav>
    );
}
